import React from "react";
import "../assets/css/statChip.css";

/**
 * Metric chip for a page header — icon, caption, value.
 *
 * Every page used to hand-roll these with inline widths and 9-10px text, so
 * the same "RECORDS" chip came out a different size on each screen. One
 * component keeps them identical and legible.
 *
 * Props:
 *   icon  : bootstrap-icon class, e.g. "bi-clipboard-data-fill"
 *   label : short caption ("RECORDS")
 *   value : the number / time to show
 *   tone? : "blue" | "green" | "orange" | "purple" | "slate" | "amber" | "red"
 *
 * A chip becomes a FILTER when it is given onClick — the overdue counts do
 * this, because a number you cannot act on is a number you learn to ignore.
 * `active` says the list is currently showing only those rows, so pressing it
 * again clears the filter rather than re-applying it. Without onClick it stays
 * a plain div, which is right: most of these are readouts, and a div that
 * looks pressable but is not is worse than one that does not.
 */
const StatChip = ({
  icon,
  label,
  value,
  tone = "blue",
  title,
  onClick,
  active = false,
}) => {
  const className = [
    "sk-stat",
    `sk-stat--${tone}`,
    onClick ? "sk-stat--button" : "",
    active ? "is-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      {icon && (
        <span className="sk-stat__icon">
          <i className={`bi ${icon}`} aria-hidden="true" />
        </span>
      )}
      <span className="sk-stat__text">
        <span className="sk-stat__label">{label}</span>
        <span className="sk-stat__value">{value}</span>
      </span>
    </>
  );

  if (!onClick) {
    return (
      <div className={className} title={title || label}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={className}
      title={title || label}
      onClick={onClick}
      aria-pressed={active}
    >
      {body}
    </button>
  );
};

export default StatChip;
